import React from 'react';

import PropTypes from 'prop-types';
import {useSnackbar} from 'notistack';
import {Form, FormikProps} from 'formik';
import {CallMade} from '@material-ui/icons';
import {Box, Grid} from '@material-ui/core';

import {LayoutServices} from 'services';
import {FieldShape} from 'commons/shapes';
import {useAlert, useSpinner} from 'hooks';
import {InferPropTypes} from 'commons/types/Types';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {EfeButtonPrimary, EfeFormSection, EfeSupportMessage} from 'components';
import {Messages, PersonTypes, Roles, SnackbarVariants, ViewChildId} from 'commons/constants';
import {EfeResponse, ScreenFilterValues, BeneficiaryServiceRequest, View} from 'commons/models';

InnerFormPersonalInfo.propTypes = {
    viewKey: PropTypes.string.isRequired,
    viewId: PropTypes.number.isRequired,
    fieldHandler: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};

function InnerFormPersonalInfo(
    props: InferPropTypes<
        typeof InnerFormPersonalInfo.propTypes & FormikProps<BeneficiaryServiceRequest>
    >
) {
    const {
        values,
        errors,
        fields,
        isSubmitting,
        setFieldValue,
        fieldHandler,
        viewId,
        validateForm,
        viewKey,
    } = props;
    const {enqueueSnackbar} = useSnackbar();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    async function getFieldVariant() {
        try {
            setIsLoading(true);
            const request: ScreenFilterValues = {
                viewId: viewId,
                personType: PersonTypes.FISICA,
                foreign: values.foreignCitizen,
                address: false,
                rol: Roles.BENEFICIARY,
                viewKey: ViewChildId.BENEFICIARY_PERSONAL_INFO,
            };
            const response: EfeResponse<View> = await LayoutServices.getViewByFilters(request);
            if (response.data && response.data.fields) {
                setIsLoading(false);
                await fieldHandler(response.data);
            } else {
                enqueueSnackbar(Messages.LAYOUT_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
                fieldHandler(undefined);
                setIsLoading(false);
            }
            validateForm();
        } catch (error) {
            setIsLoading(false);
            fieldHandler(undefined);
            showAlertError(error, Messages.LAYOUT_ERROR);
        }
    }

    React.useEffect(() => {
        if (ViewChildId.BENEFICIARY_PERSONAL_INFO == viewKey) {
            getFieldVariant();
        }
    }, [values.foreignCitizen]);

    /* eslint-disable react/jsx-handler-names */
    return (
        <Form noValidate>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            {fields && fields.length > 0 && (
                <Box m={2}>
                    <EfeSupportMessage />
                    <EfeFormSection
                        fields={fields}
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                    />
                    <Grid container direction='row' justifyContent='flex-end'>
                        <Box pt={6} width={160}>
                            <EfeButtonPrimary
                                fullWidth
                                disabled={isSubmitting}
                                type='submit'
                                endIcon={<CallMade />}>
                                {'OK'}
                            </EfeButtonPrimary>
                        </Box>
                    </Grid>
                </Box>
            )}
        </Form>
    );
}

export default InnerFormPersonalInfo;
